import React, { useContext, useEffect } from 'react'
import { useFlag, useUserSignedIn } from '@sketch/modules-common'

import { Dropdown, ModalContext } from '@sketch/components'
import DownloadOptions from './DownloadOptions'
import RenameAction from './RenameAction'
import CopyLink from './CopyLink'
import { DeleteShareModal } from '../DeleteShareModal'
import LeaveShareModal from '../LeaveShareModal'
import { ExportDesignTokensModal } from '../../modals/ExportDesignTokensModal'
import { DuplicateShareOption } from '../DuplicateShareOption'
import { getEligibleWorkspacesForDuplication } from '../../../workspace/utils/getEligibleWorkspacesForDuplication/getEligibleWorkspacesForDuplication'

import {
  GetShareQueryVariables,
  ShareListItemFragment,
  GetShareDocument,
  useGetWorkspacesForDuplicationQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'

import { AnnotationDropdownActions } from '../../../annotations/components'
import { useSharingModal } from '../../hooks/useSharingModal'
import { useDocumentInfoModal } from '../../hooks/useDocumentInfoModal'
import { useComponentsState } from '../ComponentsStateContext'
import { useApolloClient } from '@apollo/client'
import {
  DropdownOptions,
  useGetShareDropdownOptions,
} from '../../operations/useGetShareDropdownOptions'
import { useIsAnnotationContextAvailable } from '../../../annotations/hooks'
import { useCheckCurrentAuthorizationForWorkspace } from '../../../sso/operations/useCheckCurrentAuthorizationForWorkspace'
import { PinDocument, PinDocumentProps } from './PinDocument'
import { MoveShareAction } from './MoveShareAction'
import { PublishDocumentOption } from './PublishDocumentOption'
import { getShowArchiveAction } from '../../../storageItems/utils'
import { ArchiveStorageItemsModal } from '../../../storageItems'

export interface ShareForShareDropdown
  extends Pick<
    ShareListItemFragment,
    | '__typename'
    | 'identifier'
    | 'name'
    | 'project'
    | 'userCanUpdateSettings'
    | 'publicAccessLevel'
    | 'shareType'
    | 'pinnedByCurrentUserAt'
    | 'archivedAt'
    | 'deletedAt'
  > {}

export interface VersionsForDropdown {
  current: { shortId: string }
  latest: { shortId: string }
}

// This is just to preload the data if it wasn't loaded already
// It should give faster rendering speeds when any of the options is chosen
const usePrefetchGetShare = (identifier: string) => {
  const client = useApolloClient()

  useEffect(() => {
    const variables: GetShareQueryVariables = {
      shortId: identifier,
    }
    client.query({
      query: GetShareDocument,
      variables,
      fetchPolicy: 'cache-first',
    })
  }, [client, identifier])
}

interface LoadingDropdownOptions extends ShareActionsDropdownProps {
  dropdownOptions: DropdownOptions
  workspaceStatus?: WorkspaceMinimalFragment['status']
}

const DownloadOptionsLoading = (props: LoadingDropdownOptions) => {
  const { dropdownOptions, workspaceStatus } = props

  if (dropdownOptions.loading) return <Dropdown.Skeleton count={2} />
  if (dropdownOptions.error) return null

  const share = { ...props.share, ...dropdownOptions.data.share }

  return (
    <DownloadOptions
      document={dropdownOptions.data.document}
      share={share}
      download={dropdownOptions.data.download}
      isViewingLatestVersion={props.isViewingLatestVersion}
      workspaceStatus={workspaceStatus}
    />
  )
}

const LeaveShareLoading = (props: LoadingDropdownOptions) => {
  const { dropdownOptions } = props
  const { showModal } = useContext(ModalContext)

  if (dropdownOptions.loading) return <Dropdown.Skeleton />
  if (dropdownOptions.error) return null

  const share = { ...props.share, ...dropdownOptions.data.share }
  if (!share.userCanLeaveShare) return null

  return (
    <>
      <Dropdown.Divider />
      <Dropdown.Item
        intent="negative"
        onClick={() =>
          showModal(LeaveShareModal, {
            shareIdentifier: share.identifier!,
            shareName: share.name!,
          })
        }
      >
        Leave&hellip;
      </Dropdown.Item>
    </>
  )
}

const RenameShareLoading = (props: LoadingDropdownOptions) => {
  const { dropdownOptions, share } = props

  if (dropdownOptions.loading) return <Dropdown.Skeleton />
  if (dropdownOptions.error) return null

  if (!dropdownOptions.data.share.userCanEdit) return null

  const shareIdentifier = share?.identifier
  const shareName = share?.name

  return (
    <>
      <Dropdown.Divider />
      <RenameAction id={shareIdentifier} name={shareName}>
        Rename&hellip;
      </RenameAction>
    </>
  )
}

const AnnotationDropdownActionsLoading = (props: LoadingDropdownOptions) => {
  const { dropdownOptions } = props

  const isAnnotationsContextAvailable = useIsAnnotationContextAvailable()

  if (!isAnnotationsContextAvailable) {
    return null
  }

  if (dropdownOptions.loading) return <Dropdown.Skeleton />
  if (dropdownOptions.error) return null

  if (!dropdownOptions.data.share.commentsEnabled) return null

  const { identifier, subscriptionStatus } = dropdownOptions.data.share

  return (
    <>
      <Dropdown.Divider />
      <AnnotationDropdownActions
        shareIdentifier={identifier}
        subscriptionStatus={subscriptionStatus}
      />
    </>
  )
}

const DuplicateShareLoading = (props: LoadingDropdownOptions) => {
  const {
    share,
    dropdownOptions,
    workspaceIdentifier,
    onDuplicateShareToDraftsClick,
    onDuplicateShareToClick,
  } = props

  const {
    error: getWorkspacesError,
    loading: getWorkspacesLoading,
    data: getWorkspacesData,
  } = useGetWorkspacesForDuplicationQuery()

  const { checkAuthorization } = useCheckCurrentAuthorizationForWorkspace()

  if (dropdownOptions.loading || getWorkspacesLoading) {
    return <Dropdown.Skeleton />
  }

  if (dropdownOptions.error || getWorkspacesError || !getWorkspacesData) {
    return null
  }

  const workspaces = getWorkspacesData!.me.workspaces
  const userCanInspect = dropdownOptions.data.share.userCanInspect

  const { userCanDuplicate, currentWorkspace, otherWorkspaces } =
    getEligibleWorkspacesForDuplication(
      workspaces,
      dropdownOptions.data.share.workspace.identifier,
      dropdownOptions.data.share.publicAccessLevel,
      { hasAccessToWorkspaceCheckFn: checkAuthorization }
    )

  const isLoggedInWithProperSSOCredentials = currentWorkspace
    ? checkAuthorization(currentWorkspace)
    : true

  if (
    !userCanInspect ||
    !userCanDuplicate ||
    !isLoggedInWithProperSSOCredentials ||
    !dropdownOptions.data.document.sketchVersion
  ) {
    return null
  }

  const sketchVersion = dropdownOptions.data.document.sketchVersion!

  return (
    <DuplicateShareOption
      workspaceIdentifier={workspaceIdentifier}
      sketchVersion={sketchVersion}
      moreThanOneEligible={!currentWorkspace && Boolean(otherWorkspaces.length)}
      onDuplicateShareToDraftsClick={onDuplicateShareToDraftsClick}
      onDuplicateShareToClick={onDuplicateShareToClick}
      share={share}
    />
  )
}

export interface ShareActionsDropdownProps {
  share: ShareForShareDropdown
  workspaceIdentifier: string
  isViewingLatestVersion?: boolean
  versions: VersionsForDropdown
  onDuplicateShareToDraftsClick: () => void
  onDuplicateShareToClick: () => void
  pinDocumentConfig?: PinDocumentProps
  workspace?: Pick<
    WorkspaceMinimalFragment,
    'userCanAdminister' | 'userCanEdit' | 'status' | 'userRole' | 'features'
  >
  showAnnotationActions?: boolean
}

export const ShareActionsDropdown: React.FCC<
  ShareActionsDropdownProps
> = props => {
  const {
    share,
    versions,
    workspaceIdentifier,
    pinDocumentConfig,
    workspace,
    showAnnotationActions,
  } = props

  const isSignedIn = useUserSignedIn()
  const isPartialSharingEnabled = useFlag('partial-sharing')
  const isPublishToCommunityEnabled = useFlag('publish-community')

  const dropdownOptions = useGetShareDropdownOptions({
    share,
    version: versions.current,
  })

  usePrefetchGetShare(share.identifier)
  const componentsState = useComponentsState()

  const { showModal } = useContext(ModalContext)

  const { openShareSettingsModal } = useSharingModal(share)
  const { openDocumentInfoModal } = useDocumentInfoModal({
    share,
    versions,
    workspaceIdentifier,
  })

  const shareIdentifier = share?.identifier
  const shareName = share?.name
  const isActive = workspace?.status === 'ACTIVE'
  const isPublished = !!dropdownOptions.data?.share.publication
  const isUserCanPublish = !!dropdownOptions.data?.share.userCanPublish
  const canPublishDocument = isPublishToCommunityEnabled && isUserCanPublish

  const showArchive =
    workspace && getShowArchiveAction(workspace, share.project)
  const isArchived = !!share.archivedAt

  if (!share.userCanUpdateSettings) {
    return (
      <div data-testid="share-dropdown">
        <Dropdown.Header>Document</Dropdown.Header>
        <DownloadOptionsLoading
          {...props}
          dropdownOptions={dropdownOptions}
          workspaceStatus={workspace?.status}
        />
        {isSignedIn && isActive && (
          <DuplicateShareLoading {...props} dropdownOptions={dropdownOptions} />
        )}
        <Dropdown.Divider />
        <CopyLink shareIdentifier={share.identifier} />
        {isActive && (
          <>
            <RenameShareLoading {...props} dropdownOptions={dropdownOptions} />
            {showAnnotationActions && (
              <AnnotationDropdownActionsLoading
                {...props}
                dropdownOptions={dropdownOptions}
              />
            )}
            <LeaveShareLoading {...props} dropdownOptions={dropdownOptions} />
          </>
        )}
        {isActive && pinDocumentConfig && (
          <PinDocument {...pinDocumentConfig} />
        )}
      </div>
    )
  }

  const ShareDropdown = isPartialSharingEnabled ? (
    <>
      <Dropdown.Item onClick={() => openShareSettingsModal()}>
        Edit Permissions&hellip;
      </Dropdown.Item>
    </>
  ) : (
    <Dropdown.Item onClick={() => openShareSettingsModal()}>
      Share&hellip;
    </Dropdown.Item>
  )

  return (
    <div data-testid="share-dropdown">
      <Dropdown.Header>Document</Dropdown.Header>
      <DownloadOptionsLoading {...props} dropdownOptions={dropdownOptions} />

      {/* TODO: decide whether we want to show the duplicate option for archived shares
      see: https://linear.app/sketch/issue/SWEB-1220/decide-whether-we-want-to-give-option-duplicate-to-root-archived */}
      {isSignedIn && isActive && !isArchived && (
        <DuplicateShareLoading {...props} dropdownOptions={dropdownOptions} />
      )}
      <Dropdown.Divider />
      <Dropdown.Item onClick={() => openDocumentInfoModal()}>
        Settings&hellip;
      </Dropdown.Item>
      {isActive && ShareDropdown}
      <CopyLink shareIdentifier={share.identifier} />
      {isActive && (
        <>
          {canPublishDocument && (
            <PublishDocumentOption
              share={share}
              workspaceIdentifier={workspaceIdentifier}
              isPublished={isPublished}
              publicCommentsEnabled={
                dropdownOptions.data?.share.publicCommentsEnabled || false
              }
              publicInspectEnabled={
                dropdownOptions.data?.share.publicInspectEnabled || false
              }
              userCanAdminister={workspace?.userCanAdminister}
            />
          )}
          <Dropdown.Divider />
          <Dropdown.Item
            onClick={() =>
              showModal(ExportDesignTokensModal, {
                shareIdentifier: share.identifier!,
                versionShortId: versions.current.shortId!,
                userCanEditExportUrl: share.userCanUpdateSettings,
                componentsState,
              })
            }
          >
            Export&nbsp;Design&nbsp;Tokens&hellip;
          </Dropdown.Item>
          <Dropdown.Divider />
          <RenameAction id={shareIdentifier} name={shareName}>
            Rename&hellip;
          </RenameAction>

          {isActive && pinDocumentConfig && (
            <PinDocument {...pinDocumentConfig} />
          )}

          <MoveShareAction
            share={share}
            workspaceIdentifier={workspaceIdentifier}
            action={isArchived ? 'unarchive' : 'move'}
            label={isArchived ? 'Unarchive…' : 'Move To…'}
          />

          {showAnnotationActions && (
            <AnnotationDropdownActionsLoading
              {...props}
              dropdownOptions={dropdownOptions}
            />
          )}
          <Dropdown.Divider />
          {showArchive && !isArchived && (
            <Dropdown.Item
              onClick={() => {
                showModal(ArchiveStorageItemsModal, { items: [share] })
              }}
            >
              Archive&hellip;
            </Dropdown.Item>
          )}
          <Dropdown.Item
            intent="negative"
            onClick={() =>
              showModal(DeleteShareModal, {
                name: shareName,
                shareShortId: shareIdentifier,
              })
            }
          >
            Delete&hellip;
          </Dropdown.Item>
        </>
      )}
    </div>
  )
}
